import { useState, useEffect } from 'react';
import { Driver } from '../../../redux/api/driver/driver.model';
import { INotifyDriverWithFullSegment } from '../TwNotifyDriver';
import { TwAutocompleteOption } from '../../TwAutocomplete/TwAutocomplete';

interface UseDriverSelectionProps {
  driver: INotifyDriverWithFullSegment;
  driversList: Driver[];
  handleUpdateDriver: (
    id: string,
    radio: string,
    segmentId: string,
    removed: boolean,
    oldDriverId?: string,
    driverName?: string,
    deletedSegmentIndex?: number,
    textMessage?: string
  ) => void;
}

const useDriverSelection = ({
  driver,
  driversList,
  handleUpdateDriver
}: UseDriverSelectionProps): {
  selectedDriverOption: TwAutocompleteOption | null;
  selectedDriver: Driver | undefined;
  radio: string;
  setRadio: (radio: string) => void;
  driversListOptions: TwAutocompleteOption[];
  handleDriverChange: (
    value: TwAutocompleteOption | null,
    removed: boolean,
    oldDriverId?: string
  ) => void;
  textMessage: string;
  handleTextMessageChange: (newTextMessage: string) => void;
} => {
  const [selectedDriverOption, setSelectedDriverOption] = useState<TwAutocompleteOption | null>(
    null
  );
  const [selectedDriver, setSelectedDriver] = useState<Driver | undefined>(undefined);
  const [radio, setRadio] = useState('');
  const [textMessage, setTextMessage] = useState(driver.textMessage || '');

  useEffect(() => {
    setTextMessage(driver.textMessage || '');
  }, [driver.textMessage]);
  

  const [driversListOptions, setDriversListOptions] = useState<TwAutocompleteOption[]>([]);

  // Initialize drivers list options
  useEffect(() => {
    if (driversList.length > 0) {
      const newDrivers: TwAutocompleteOption[] = driversList.map((driver) => ({
        id: driver.id,
        label: `${driver.first_name} ${driver.last_name}`,
        value: driver.id
      }));
      setDriversListOptions(newDrivers);
    }
  }, [driversList]);

  // Set initial driver and radio selection
  useEffect(() => {
    if (driversList.length > 0) {
      const fullDriverInfo = driversList.find(
        (completedriver) => completedriver.id === driver.driverId
      );

      setSelectedDriver(fullDriverInfo);

      const radioOption = returnRadioOption(fullDriverInfo);

      if (fullDriverInfo?.phone) {
        setRadio('phone');
        setTextMessage(driver.textMessage || '');
      } else {
        fullDriverInfo?.email_address && setRadio('email');
      }

      handleUpdateDriver(
        fullDriverInfo?.id || '',
        radioOption,
        driver.fullSegment.id || '',
        false,
        undefined,
        fullDriverInfo?.name,
        driver.deleted_trip_index,
        driver.textMessage
      );
    }
  }, [driversList, driver]);

  // Set selected driver option when options change
  useEffect(() => {
    if (driversListOptions.length > 0) {
      const driverOption = driversListOptions.find((driverOpt) => driverOpt.id === driver.driverId);
      setSelectedDriverOption(driverOption || null);
    }
  }, [driversListOptions, driver.driverId]);

  const handleDriverChange = (
    value: TwAutocompleteOption | null,
    removed: boolean,
    oldDriverId?: string
  ): void => {
    const driverInfo = driversList.find((driver) => driver.id === value?.id);
    setSelectedDriver(driverInfo || undefined);

    if (removed) {
      setRadio('');
    } else {
      setSelectedDriverOption(value);
      if (driverInfo?.phone) {
        setRadio('phone');
      } else if (driverInfo?.email_address) {
        setRadio('email');
      } else {
        setRadio('');
      }
    }

    handleUpdateDriver(
      driverInfo?.id || '',
      returnRadioOption(driverInfo),
      driver.fullSegment.id || '',
      removed,
      oldDriverId
    );

    // Clear autocomplete after removal
    removed && setSelectedDriverOption(null);
  };

  const returnRadioOption = (driverInfo: Driver | undefined): string => {
    if (driverInfo?.phone) {
      return 'phone';
    } else if (driverInfo?.email_address) {
      return 'email';
    }
    return '';
  };

  const handleTextMessageChange = (newTextMessage: string): void => {
    setTextMessage(newTextMessage);

    // Only update if we have a selected driver and radio is 'phone'
    if (selectedDriver && radio === 'phone') {
      handleUpdateDriver(
        selectedDriver.id,
        radio,
        driver.fullSegment.id || '',
        false,
        undefined,
        selectedDriver.name,
        driver.deleted_trip_index,
        newTextMessage
      );
    }
  };
  return {
    selectedDriverOption,
    selectedDriver,
    radio,
    setRadio,
    driversListOptions,
    handleDriverChange,
    textMessage,
    handleTextMessageChange
  };
};

export default useDriverSelection;
